import { type VariantProps, cva } from 'class-variance-authority';
import { type ComponentPropsWithoutRef, type FC } from 'react';
import { cn } from '../lib/utils';
import {
  Item as ShadcnItem,
  ItemContent as ShadcnItemContent,
  ItemDescription as ShadcnItemDescription,
  ItemTitle as ShadcnItemTitle,
} from './ui/item';

const itemWrapperVariants = cva('', {
  variants: {
    variant: {
      default: 'bg-background-base',
      outline: 'bg-background-base border-border',
      muted: 'bg-background-muted',
      // 1px border + 1px shadow (no layout shift)
      highlighted: 'bg-background-base border-primary ring-inset ring-1 ring-primary',
      disabled: 'cursor-not-allowed bg-background-muted text-foreground-muted',
    },
  },
  defaultVariants: {
    variant: 'outline',
  },
});

type ShadcnItemProps = ComponentPropsWithoutRef<typeof ShadcnItem>;
type WrapperVariants = VariantProps<typeof itemWrapperVariants>;

// Omit shadcn's variant to use our own
export interface ItemComponentProps extends Omit<ShadcnItemProps, 'variant'>, WrapperVariants {}

/**
 * Item - A wrapper around shadcn Item with additional variants
 *
 * Variants:
 * - `default` - transparent background
 * - `outline` - bordered with hover effect
 * - `muted` - muted background
 * - `highlighted` - primary inset border with subtle hover (for toggleable lists)
 * - `disabled` - muted background with not-allowed cursor
 */
export const Item: FC<ItemComponentProps> = ({ variant, className, ...props }) => {
  // 'highlighted' and 'disabled' aren't shadcn variants, map to 'default' (our wrapper handles the styling)
  const shadcnVariant = variant === 'highlighted' || variant === 'disabled' ? 'default' : variant;

  return (
    <ShadcnItem
      variant={shadcnVariant}
      className={cn(itemWrapperVariants({ variant }), className)}
      {...props}
    />
  );
};

export const ItemContent: FC<ComponentPropsWithoutRef<typeof ShadcnItemContent>> = ({
  className,
  ...props
}) => <ShadcnItemContent className={cn('gap-0', className)} {...props} />;

export const ItemTitle: FC<ComponentPropsWithoutRef<typeof ShadcnItemTitle>> = ({
  className,
  ...props
}) => <ShadcnItemTitle className={cn('heading-sm', className)} {...props} />;

export const ItemDescription: FC<ComponentPropsWithoutRef<typeof ShadcnItemDescription>> = ({
  className,
  ...props
}) => (
  <ShadcnItemDescription
    className={cn(
      'line-clamp-none w-full text-pretty text-foreground-secondary body-md',
      className,
    )}
    {...props}
  />
);

export {
  ItemActions,
  ItemFooter,
  ItemGroup,
  ItemHeader,
  ItemMedia,
  ItemSeparator,
} from './ui/item';
